import { Time } from '@/common/models/Time';
import TimeRange from '@/common/models/TimeRange';

const FULL_SCHEDULE = TimeRange.fromObject([
  Time.fromObject({ hours: 0, minutes: 0, seconds: 0 }),
  Time.fromObject({ hours: 24, minutes: 0, seconds: 0 }),
]);

const DAY_DURATION = FULL_SCHEDULE.duration;

/**
 * Array of TimeRnages containing a TimeRange per hour of a day
 */
export const HOUR_RANGES = Array.from(Array(24).keys()).map((hourIndex) =>
  TimeRange.fromObject([{ hours: hourIndex }, { hours: hourIndex + 1 }]),
);
/**
 * Array of TimeRanges containing a TimeRange per minute of a day
 */
const MINUTE_RANGES = Array.from(Array(24 * 60).keys()).map((minutes) =>
  TimeRange.fromObject([{ minutes }, { minutes: minutes + 1 }]),
);

class DaySchedule {
  private timeRanges: TimeRange[];

  constructor(ranges: TimeRange[] = [FULL_SCHEDULE]) {
    this.timeRanges = ranges;
  }

  public get scheduledRanges(): Readonly<TimeRange[]> {
    return this.timeRanges;
  }

  public isEmpty(): boolean {
    return this.timeRanges.length === 0;
  }

  public isFull(): boolean {
    if (this.timeRanges.length !== 1) {
      return false;
    }
    const [range] = this.timeRanges;
    return range.duration.valueOf() === DAY_DURATION.valueOf();
  }

  public isTimeFree(time: Time): boolean {
    return this.timeRanges.every((range) => range.to < time || range.from > time);
  }

  public isRangeFree(timeRange: TimeRange): boolean {
    return this.timeRanges.every((range) => range.to <= timeRange.from || range.from >= timeRange.to);
  }

  public clearDay() {
    this.timeRanges = [];
  }

  public bookFullDay() {
    this.timeRanges = [FULL_SCHEDULE];
  }

  public scheduleRange(newRange: TimeRange) {
    if (this.timeRanges.length === 0) {
      this.timeRanges = [newRange];
    }

    // Sort the ranges by their 'from' property.
    const sortedRanges = [newRange, ...this.timeRanges].sort((a, b) => a.from.valueOf() - b.from.valueOf());

    const mergedRanges: TimeRange[] = [sortedRanges[0]];

    for (let i = 1; i < sortedRanges.length; i += 1) {
      const currentRange = sortedRanges[i];
      const lastMergedRange = mergedRanges[mergedRanges.length - 1];

      if (currentRange.from > lastMergedRange.to) {
        // If the current range starts after the last merged range ends, simply add it.
        mergedRanges.push(currentRange);
      } else {
        // If there is an overlap, merge the current range with the last merged range.
        const newLastMergedRange = TimeRange.fromObject([
          lastMergedRange.from,
          Time.fromMilliseconds(Math.max(lastMergedRange.to.valueOf(), currentRange.to.valueOf())),
        ]);
        mergedRanges[mergedRanges.length - 1] = newLastMergedRange;
      }
    }

    this.timeRanges = mergedRanges;
  }

  public freeRange(rangeToDeselect: TimeRange) {
    const newRanges = [];

    const sortedRanges = this.timeRanges.sort((a, b) => a.from.valueOf() - b.from.valueOf());

    for (const range of sortedRanges) {
      // rangeToDeselect is overlapping range, remove range
      if (rangeToDeselect.from <= range.from && rangeToDeselect.to >= range.to) {
        continue;
      }
      // rangeToDeselect is intersecting with begining of range, cut range start
      if (rangeToDeselect.from <= range.from && rangeToDeselect.to > range.from && rangeToDeselect.to < range.to) {
        const truncatedRange = TimeRange.fromObject([rangeToDeselect.to, range.to]);
        newRanges.push(truncatedRange);
        continue;
      }
      // rangeToDeselect is cutting range in half, split range in two
      if (rangeToDeselect.from > range.from && rangeToDeselect.to < range.to) {
        const firstHalf = TimeRange.fromObject([range.from, rangeToDeselect.from]);
        const secondHalf = TimeRange.fromObject([rangeToDeselect.to, range.to]);
        newRanges.push(...[firstHalf, secondHalf]);
        continue;
      }
      // rangeToDeselect is intersecting with end of range, cut range end
      if (rangeToDeselect.from > range.from && rangeToDeselect.from < range.to && rangeToDeselect.to >= range.to) {
        const truncatedRange = TimeRange.fromObject([range.from, rangeToDeselect.from]);
        newRanges.push(truncatedRange);
        continue;
      }

      newRanges.push(range);
    }

    this.timeRanges = newRanges;
  }

  public toString(timeUnit: 'hour' | 'minute' = 'hour') {
    let output = '';
    for (let hour = 0; hour < 24; hour += 1) {
      if (timeUnit === 'minute') {
        for (let minute = 0; minute < 60; minute += 1) {
          const minuteIndex = hour * 60 + minute;
          const minuteRange = MINUTE_RANGES[minuteIndex];
          output += this.isRangeFree(minuteRange) ? '0' : '1';
        }
      } else {
        const hourRange = HOUR_RANGES[hour];
        output += this.isRangeFree(hourRange) ? '0' : '1';
      }
    }

    return output;
  }

  public static parse(daypart: string) {
    const isFull = daypart.indexOf('0') === -1;
    if (isFull) {
      return new DaySchedule([FULL_SCHEDULE]);
    }

    const isEmpty = daypart.indexOf('1') === -1;
    if (isEmpty) {
      return new DaySchedule([]);
    }

    let timeUnit: 'hour' | 'minute';
    if (daypart.length === 24) {
      timeUnit = 'hour';
    } else if (daypart.length === 24 * 60) {
      timeUnit = 'minute';
    } else {
      throw Error('Cannot parse daypart - Invalid length');
    }

    const timeRanges = [];
    let rangeStartIndex: number;
    for (let i = 0; i < daypart.length; i += 1) {
      if (rangeStartIndex == null && daypart[i] === '1') {
        rangeStartIndex = i;
      }
      if (rangeStartIndex != null) {
        if (daypart[i] === '0') {
          const timeRange =
            timeUnit === 'minute'
              ? TimeRange.fromObject([{ minutes: rangeStartIndex }, { minutes: i }])
              : TimeRange.fromObject([{ hours: rangeStartIndex }, { hours: i }]);
          timeRanges.push(timeRange);
          rangeStartIndex = null;
        } else if (i === daypart.length - 1) {
          const timeRange =
            timeUnit === 'minute'
              ? TimeRange.fromObject([{ minutes: rangeStartIndex }, { minutes: daypart.length }])
              : TimeRange.fromObject([{ hours: rangeStartIndex }, { hours: daypart.length }]);
          timeRanges.push(timeRange);
          rangeStartIndex = null;
        }
      }
    }
    return new DaySchedule(timeRanges);
  }
}

export default DaySchedule;
